import { useCallback, useState } from "react";
import {
  CHECK_AGE_ELIGIBILITY_CODE,
  checkCountryAgeEligibility,
  checkCountryAgeEligibilityForAffiliate,
} from "../services/app";

/**
 * useAgeRestriction
 * @param {Object} params
 * @param {Function} params.onRequireUpdate - Called if age is not found (code 1001)
 * @param {Function} params.onEligible - Called if age is within eligible range
 * @param {Function} params.onIneligible - Called if age is not in eligible range (code 1004)
 * @param {Function} [params.onError] - Called if an error occurs in API call
 * @param {boolean} [params.isAffiliate] - If true, use checkCountryAgeEligibilityForAffiliate
 * @returns {Object} { checkAge, isLoading }
 */
export default function useAgeRestriction({
  onRequireUpdate,
  onEligible,
  onIneligible,
  onError,
  isAffiliate = false,
}) {
  const [isLoading, setIsLoading] = useState(false);

  const checkAge = useCallback(
    async (onEligibleFn) => {
      try {
        setIsLoading(true);
        const res = isAffiliate
          ? await checkCountryAgeEligibilityForAffiliate()
          : await checkCountryAgeEligibility();

        const handleEligible = () => {
          if (typeof onEligible === "function") onEligible();
          if (typeof onEligibleFn === "function") onEligibleFn();
        };

        if (res && res.success === true) {
          handleEligible();
        } else if (res && res.success === false) {
          if (
            res.code === CHECK_AGE_ELIGIBILITY_CODE.USER_AGE_NOT_FOUND &&
            typeof onRequireUpdate === "function"
          ) {
            onRequireUpdate();
          } else if ( // In all other case, user age is not eligible
            typeof onIneligible === "function"
          ) {
            onIneligible();
          }
        }
      } catch (error) {
        console.error("Error checking age restriction:", error);
        if (typeof onError === "function") onError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [onRequireUpdate, onEligible, onIneligible, onError, isAffiliate]
  );

  return { checkAge, isLoading };
}
